import { FliptClient } from "@flipt-io/flipt";
import {
	BatchEvaluationResponse,
	BooleanEvaluationResponse,
	VariantEvaluationResponse,
} from "@flipt-io/flipt/dist/evaluation/models";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { getUser } from "@/auth";
import {
	FLIPT_FLAGS,
	FLIPT_NAMESPACE,
	FLIPT_URL,
	IS_ON_PREMISE,
} from "@/constants/constants";
import { FliptFlagType } from "@/types";

type FlagType = "boolean" | "variant";

type GetFlagProps = {
	flagKey: keyof typeof FLIPT_FLAGS | string;
	flagType?: FlagType;
};

type GetFliptFlag = <T extends FlagType = "boolean">(props: {
	flagKey: keyof typeof FLIPT_FLAGS;
	flagType?: T;
}) => Promise<
	T extends "boolean" ? BooleanEvaluationResponse : VariantEvaluationResponse
>;

type GetFliptFlags = (
	props: GetFlagProps[],
) => Promise<BatchEvaluationResponse>;

const getFliptFlag: GetFliptFlag = async <T>({
	flagKey = "",
	flagType = "boolean",
}: GetFlagProps) => {
	const client = new FliptClient({
		url: FLIPT_URL,
	});

	const response = await client.evaluation[flagType as FlagType]({
		flagKey,
		namespaceKey: FLIPT_NAMESPACE,
		entityId: nanoid(),
		context: {
			tenant_id: getUser()?.fraud_tenant_id,
		},
	});

	return response as T extends "boolean"
		? BooleanEvaluationResponse
		: VariantEvaluationResponse;
};

const getFliptFlags: GetFliptFlags = async (flags: GetFlagProps[]) => {
	const client = new FliptClient({
		url: FLIPT_URL,
	});

	const response = await client.evaluation.batch({
		requests: flags.map((f) => ({
			namespaceKey: FLIPT_NAMESPACE,
			flagKey: f.flagKey,
			entityId: nanoid(),
			context: {
				tenant_id: getUser()?.fraud_tenant_id,
			},
		})),
	});

	return response;
};

const useGetFliptBooleanFlag = (flag: keyof typeof FLIPT_FLAGS) => {
	const [isEnabled, setIsEnabled] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const getFlags = async () => {
		if (IS_ON_PREMISE) {
			setIsEnabled(false);
			setIsLoading(false);
			return;
		}

		try {
			const evaluation = await getFliptFlag({
				flagKey: flag,
			});
			setIsEnabled(evaluation.enabled);
		} catch (error) {
			setIsEnabled(false);
		}

		setIsLoading(false);
	};

	useEffect(() => {
		getFlags();
	}, []);

	return { isEnabled, isLoading };
};

const useGetFliptBatchFlags = (flags: (keyof typeof FLIPT_FLAGS)[]) => {
	const [flagList, setFlagList] = useState<FliptFlagType>();
	const [isLoading, setIsLoading] = useState(true);

	const getFlags = async () => {
		if (IS_ON_PREMISE) {
			setFlagList({} as any);
			setIsLoading(false);
			return;
		}

		try {
			const evaluation = await getFliptFlags(
				flags.map((f) => ({ flagKey: f })),
			);

			setFlagList(
				evaluation.responses.reduce((acc, r) => {
					const key = r.booleanResponse?.flagKey as keyof typeof FLIPT_FLAGS;
					if (key) {
						(acc as FliptFlagType) = {
							...acc,
							[key]: r.booleanResponse?.enabled ?? false,
						};
					}
					return acc;
				}, {} as FliptFlagType),
			);
		} catch (error) {
			setFlagList({} as any);
		}

		setIsLoading(false);
	};

	useEffect(() => {
		getFlags();
	}, []);

	return { flagList, isLoading };
};

export {
	getFliptFlag,
	getFliptFlags,
	useGetFliptBooleanFlag,
	useGetFliptBatchFlags,
};
